import PropTypes from 'prop-types';
import { isValidAvatar } from '../utils/avatar';
import './RoomAvatar.css';

/**
 * RoomAvatar component - Pure presentational component for displaying room avatars
 * 
 * Displays avatars in one of three modes:
 * 1. Preview mode (previewUrl provided) - Shows a preview of pending changes
 * 2. Avatar mode (avatarUrls provided) - Shows current state (1-2 avatars or placeholder)
 * 3. Placeholder mode (no avatars) - Shows initial letter
 * 
 * @param {Array<string>} avatarUrls - Array of 0-2 avatar URLs to display (current state)
 * @param {string} previewUrl - Optional preview URL for pending changes (overrides avatarUrls)
 * @param {string} placeholderText - Text to show in placeholder (default: '?')
 */
const RoomAvatar = ({ 
  avatarUrls = [], 
  previewUrl = null,
  placeholderText = '?',
}) => {
  // Get initial letter for placeholder
  const placeholderInitial = placeholderText?.charAt(0)?.toUpperCase() || '?';

  // If previewUrl is provided, use it directly (for pending avatar changes)
  if (previewUrl !== null && isValidAvatar(previewUrl)) {
    return (
      <div className="room-avatar-container">
        <img 
          className="room-avatar-single" 
          src={previewUrl} 
          alt="Room Avatar Preview" 
          crossOrigin="anonymous" 
        />
      </div>
    );
  }

  // Extract and filter valid avatar URLs
  const validAvatarUrls = avatarUrls
    .map(avatar => {
      // Handle both string URLs and objects with url property
      if (typeof avatar === 'string') {
        return avatar;
      }
      return avatar?.url;
    })
    .filter(url => url && isValidAvatar(url));

  // No avatars - show placeholder
  if (validAvatarUrls.length === 0) {
    return (
      <div className="room-avatar-container">
        <div className="room-avatar-placeholder">{placeholderInitial}</div>
      </div>
    );
  }

  // Single avatar case
  if (validAvatarUrls.length === 1) {
    return (
      <div className="room-avatar-container">
        <img 
          className="room-avatar-single" 
          src={validAvatarUrls[0]} 
          alt="Room Avatar" 
          crossOrigin="anonymous" 
        />
      </div>
    );
  }

  // Dual avatar case (2 or more valid avatars)
  if (validAvatarUrls.length >= 2) {
    return (
      <div className="room-avatar-container">
        <img 
          className="room-avatar-first" 
          src={validAvatarUrls[0]} 
          alt="Avatar 1" 
          crossOrigin="anonymous" 
        />
        <img 
          className="room-avatar-second" 
          src={validAvatarUrls[1]} 
          alt="Avatar 2" 
          crossOrigin="anonymous" 
        />
      </div>
    );
  }

  // Fallback to placeholder (should not reach here due to earlier check)
  return (
    <div className="room-avatar-container">
      <div className="room-avatar-placeholder">{placeholderInitial}</div>
    </div>
  );
};

RoomAvatar.propTypes = {
  avatarUrls: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ]),
  ),
  previewUrl: PropTypes.string,
  placeholderText: PropTypes.string,
};

export default RoomAvatar;
